/**
 * Higher / Lower Audio Clue system.
 * Plays two short notes indicating whether the next hidden card is
 * Higher, Lower, or Equal — without revealing it.
 *
 * Prefer uploaded note files from Sound System (clue_c3, clue_d3, …).
 * Falls back to synthesized sine tones when a note has no audio_url.
 *
 * Extend AUDIO_CLUE_NOTES in constants/audioClueNotes.js to add pitches.
 */

import { isEffectivelyMuted, playSound } from "./gameAudio";
import {
  NOTES,
  noteToActionKey,
  AUDIO_CLUE_NOTES,
} from "../constants/audioClueNotes";

export { NOTES, noteToActionKey, AUDIO_CLUE_NOTES };

/** Semitone steps between reference and second note (2 = whole tone, e.g. C→D). */
export const INTERVAL_STEPS = 2;

const NOTE_DURATION_SEC = 0.28;
const NOTE_GAP_MS = 120;
const SYNTH_VOLUME = 0.18;

const NOTE_BASE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

let audioContext = null;

function getAudioContext() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext) audioContext = new Ctx();
  return audioContext;
}

export function noteToFrequency(noteName) {
  const match = String(noteName).match(/^([A-G])(#|b)?(\d+)$/i);
  if (!match) return null;
  const letter = match[1].toUpperCase();
  const accidental = match[2] || "";
  const octave = parseInt(match[3], 10);
  let semitone = NOTE_BASE[letter];
  if (accidental === "#") semitone += 1;
  if (accidental === "b") semitone -= 1;
  const midi = (octave + 1) * 12 + semitone;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function noteIndex(noteName) {
  return NOTES.indexOf(noteName);
}

/**
 * Pick a random reference note with room to move in `direction`.
 * For "higher", never pick the top pitches (no room to go up).
 * For "lower", never pick the bottom pitches.
 */
export function getRandomReferenceNote(direction = "equal") {
  const last = NOTES.length - 1;
  let min = 0;
  let max = last;

  if (direction === "higher") {
    max = last - INTERVAL_STEPS;
  } else if (direction === "lower") {
    min = INTERVAL_STEPS;
  }

  if (max < min) {
    min = 0;
    max = last;
  }

  const idx = min + Math.floor(Math.random() * (max - min + 1));
  return NOTES[idx];
}

export function getHigherNote(reference) {
  const idx = noteIndex(reference);
  if (idx < 0) return null;
  const next = idx + INTERVAL_STEPS;
  if (next > NOTES.length - 1) return null;
  return NOTES[next];
}

export function getLowerNote(reference) {
  const idx = noteIndex(reference);
  if (idx < 0) return null;
  const prev = idx - INTERVAL_STEPS;
  if (prev < 0) return null;
  return NOTES[prev];
}

function compareCardRanks(currentCard, hiddenCard) {
  const a = currentCard?.value;
  const b = hiddenCard?.value;
  if (a == null || b == null) return null;
  if (b > a) return "higher";
  if (b < a) return "lower";
  return "equal";
}

function getNoteSoundConfig(noteName, soundMap) {
  if (!soundMap) return null;
  const actionKey = noteToActionKey(noteName);
  if (!actionKey) return null;
  const cfg = soundMap[actionKey];
  if (!cfg?.audio_url) return null;
  return cfg;
}

function playUploadedNote(cfg) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const audio = playSound(cfg.audio_url, {
      volume: cfg.volume ?? 0.8,
      loop: false,
    });
    if (!audio) {
      finish();
      return;
    }
    audio.addEventListener("ended", finish);
    audio.addEventListener("error", finish);
    setTimeout(finish, 2500);
  });
}

function scheduleTone(ctx, frequency, startTime, duration) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(frequency, startTime);

  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(SYNTH_VOLUME, startTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.02);
}

async function playSynthesizedNote(noteName) {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }

  const freq = noteToFrequency(noteName);
  if (freq == null) return;

  const t0 = ctx.currentTime + 0.02;
  scheduleTone(ctx, freq, t0, NOTE_DURATION_SEC);
  await new Promise((r) => setTimeout(r, (NOTE_DURATION_SEC + 0.05) * 1000));
}

async function playSingleNote(noteName, soundMap) {
  const cfg = getNoteSoundConfig(noteName, soundMap);
  if (cfg) {
    await playUploadedNote(cfg);
    return;
  }
  await playSynthesizedNote(noteName);
}

/**
 * Resolve reference + comparison notes for a visible/hidden card pair.
 * Same pair is reused if the player clicks either Clue button again this round.
 */
export function resolveClueNotes(currentCard, hiddenCard) {
  const direction = compareCardRanks(currentCard, hiddenCard);
  if (!direction) return null;

  const reference = getRandomReferenceNote(direction);
  let second = reference;

  if (direction === "higher") {
    second = getHigherNote(reference) || reference;
  } else if (direction === "lower") {
    second = getLowerNote(reference) || reference;
  }

  return { direction, reference, second };
}

/**
 * Play a single clue note (uploaded file or synth fallback).
 */
export async function playClueNote(noteName, soundMap) {
  if (isEffectivelyMuted()) return;
  await playSingleNote(noteName, soundMap);
}

/**
 * Play two short notes in sequence (reference → comparison).
 * @param {string} note1
 * @param {string} note2
 * @param {Record<string, { audio_url?: string, volume?: number }>|undefined} soundMap
 *   Map of action_key → sound config from useGameSounds (optional).
 */
export async function playTwoNotes(note1, note2, soundMap) {
  if (isEffectivelyMuted()) return;

  await playSingleNote(note1, soundMap);
  await new Promise((r) => setTimeout(r, NOTE_GAP_MS));
  await playSingleNote(note2, soundMap);
}

/**
 * Compare current visible vs next hidden by rank only (suits ignored),
 * then play both clue notes in sequence.
 * @param {object} currentCard
 * @param {object} hiddenCard
 * @param {Record<string, object>|undefined} soundMap from useGameSounds
 */
export async function playHint(currentCard, hiddenCard, soundMap) {
  const notes = resolveClueNotes(currentCard, hiddenCard);
  if (!notes) return;

  await playTwoNotes(notes.reference, notes.second, soundMap);
  return notes;
}
